/**
 * UserDropdown — Sprint 1.5 topbar avatar dropdown.
 *
 * Click avatar in topbar → this 10-item menu opens.
 *
 *   ┌────────────────────────────────┐
 *   │ Aalpen Patel                    │
 *   │ aalpen.patel@gmail.com          │
 *   │ [OWNER]                         │
 *   ├────────────────────────────────┤
 *   │ 👤  Account                     │
 *   │ ⚙   Settings                    │
 *   │ 💳  Billing & plan              │
 *   │ 📜  Activity log                │
 *   │ 🛟  Help & support              │
 *   ├────────────────────────────────┤
 *   │ 🛡  ADMIN ONLY                  │  (role-gated: platform_admin)
 *   │ 🛡  Platform Admin              │
 *   │ ❤   Platform health             │  (NEW stub /admin/health)
 *   │ 🌐  Architecture                │
 *   │ 💵  Cost Estimate               │
 *   ├────────────────────────────────┤
 *   │ 🚪  Sign out                    │  (danger)
 *   └────────────────────────────────┘
 *
 * Per operator decisions (2026-05-28):
 *   Q1: 2 stub Settings sub-routes built (AI usage + Security & sessions);
 *       Preferences + Integrations dropped → dropdown lost "Integrations"
 *   Q3: "Switch language" dropped (Nexus is English-only)
 *   Q4: Production labels (Platform Admin / Architecture / Cost Estimate)
 *       + ADD Platform health stub
 *
 * Visibility rules:
 *   - Personal section always visible
 *   - Admin section visible only when user.isPlatformAdmin (matches sidebar's prior gate)
 *   - Sign out always visible
 *
 * Close behaviors:
 *   - Click outside (backdrop) → close
 *   - Press Escape → close
 *   - Click any item → close (item handles its own navigation)
 *
 * Frontend hiding is UX only. Backend role-gates on each admin route still
 * enforce the actual permission boundary.
 */
import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Settings, CreditCard, History, LifeBuoy,
  Shield, HeartPulse, Network, DollarSign, LogOut,
} from 'lucide-react';
import { useAppStore } from '@/stores/useAppStore';
import { useRole } from '@/hooks/useRole';
import { signOut } from '@/lib/auth';

interface UserDropdownProps {
  open: boolean;
  onClose: () => void;
}

export function UserDropdown({ open, onClose }: UserDropdownProps) {
  const { user, logout: clearStore } = useAppStore();
  const { role, isPlatformAdmin } = useRole();
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Close on click outside
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    // Defer one tick so the opening click doesn't immediately close it
    const id = setTimeout(() => document.addEventListener('mousedown', onClick), 0);
    return () => {
      clearTimeout(id);
      document.removeEventListener('mousedown', onClick);
    };
  }, [open, onClose]);

  const handleSignOut = () => {
    signOut();
    clearStore();
    window.location.href = '/login';
  };

  if (!open) return null;

  const displayName = user?.fullName ?? 'Account';
  const displayEmail = user?.email ?? '';
  const displayRole = role
    ? role.split('_').map((s) => s[0].toUpperCase() + s.slice(1)).join(' ').toUpperCase()
    : '';

  // Item row component for consistent styling
  const itemCls = 'flex items-center gap-3 px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-primary-50 dark:hover:bg-slate-700 cursor-pointer transition-colors';
  const dangerCls = 'flex items-center gap-3 px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 cursor-pointer transition-colors w-full text-left';

  return (
    <div
      ref={dropdownRef}
      role="menu"
      data-testid="user-dropdown"
      className="absolute right-0 top-full mt-1 w-64 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg z-50 py-1 overflow-hidden"
    >
      {/* Header */}
      <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700">
        <div className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate" title={displayName}>
          {displayName}
        </div>
        {displayEmail && (
          <div className="text-xs text-slate-500 dark:text-slate-400 truncate mt-0.5" title={displayEmail}>
            {displayEmail}
          </div>
        )}
        {displayRole && (
          <span className="inline-block mt-2 px-2 py-0.5 text-[10px] font-bold tracking-wider rounded bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300">
            {displayRole}
          </span>
        )}
      </div>

      {/* Personal section. 2026-06-02: de-duped — "Account" and "Settings" both
          pointed at /settings. One "Settings" entry → the /settings hub, which
          now indexes every settings area (Security, Notifications, AI usage,
          Billing, Personal tax, API keys). */}
      <div className="py-1">
        <Link to="/settings" className={itemCls} onClick={onClose} role="menuitem">
          <Settings className="w-4 h-4 shrink-0" />
          <span>Account &amp; settings</span>
        </Link>
        <Link to="/billing" className={itemCls} onClick={onClose} role="menuitem">
          <CreditCard className="w-4 h-4 shrink-0" />
          <span>Billing &amp; plan</span>
        </Link>
        <Link to="/activity" className={itemCls} onClick={onClose} role="menuitem">
          <History className="w-4 h-4 shrink-0" />
          <span>Activity log</span>
        </Link>
        <Link to="/help" className={itemCls} onClick={onClose} role="menuitem">
          <LifeBuoy className="w-4 h-4 shrink-0" />
          <span>Help &amp; support</span>
        </Link>
      </div>

      {/* Admin section (platform_admin only — frontend hide; routes also role-gated) */}
      {isPlatformAdmin && (
        <>
          <div className="border-t border-slate-200 dark:border-slate-700" />
          <div className="px-4 py-2 flex items-center gap-1.5">
            <Shield className="w-3 h-3 text-amber-700 dark:text-amber-400" />
            <span className="text-[10px] font-bold tracking-wider uppercase text-amber-700 dark:text-amber-400">
              Admin only
            </span>
          </div>
          <div className="pb-1" data-testid="user-dropdown-admin-section">
            <Link to="/admin/platform" className={itemCls} onClick={onClose} role="menuitem">
              <Shield className="w-4 h-4 shrink-0" />
              <span>Platform Admin</span>
            </Link>
            <Link to="/admin/health" className={itemCls} onClick={onClose} role="menuitem">
              <HeartPulse className="w-4 h-4 shrink-0" />
              <span>Platform health</span>
            </Link>
            <Link to="/admin/architecture" className={itemCls} onClick={onClose} role="menuitem">
              <Network className="w-4 h-4 shrink-0" />
              <span>Architecture</span>
            </Link>
            <Link to="/admin/costs" className={itemCls} onClick={onClose} role="menuitem">
              <DollarSign className="w-4 h-4 shrink-0" />
              <span>Cost Estimate</span>
            </Link>
          </div>
        </>
      )}

      {/* Sign out (danger) */}
      <div className="border-t border-slate-200 dark:border-slate-700" />
      <div className="py-1">
        <button
          type="button"
          onClick={() => { onClose(); handleSignOut(); }}
          className={dangerCls}
          role="menuitem"
          data-testid="user-dropdown-signout"
        >
          <LogOut className="w-4 h-4 shrink-0" />
          <span>Sign out</span>
        </button>
      </div>
    </div>
  );
}
